import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import {
  Autocomplete,
  Box,
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import backArrow from "../../../assets/icons/backArrow.svg";
import { CustomButton } from "../../../global/components";
import CustomLoader from "../../../global/components/CustomLoader/CustomLoader";
import strings from "../../../global/constants/StringConstants";
import {
  capitalizeWords,
  formatDateTime,
  handleErrorMessage,
  openSuccessNotification,
} from "../../../helpers/methods";
import history from "../../../utils/history";
import { theme } from "../../../utils/styles";
import { AstroReportFilters } from "../../../models/interfaces";
import { getProfile } from "../../Dashboard/DashboardService";
import AstrocartographyFilterDialog from "./components/AstrocartographyFilterDialog";
import timeAnalysisStyles from "./TimeAnalysis.styles";
import {
  downloadAstrocartographyReport,
  downloadProfessionAnalysis,
  downloadTimeAnalysis,
  downloadTrueLookReport,
  downloadTrueTattooReport,
  getProfessionAnalysisReport,
} from "./TimeAnalysisService";

const TimeAnalysis = () => {
  const classes = timeAnalysisStyles;
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProfessionLoading, setIsProfessionLoading] =
    useState<boolean>(false);
  const [isTrueLookLoading, setIsTrueLookLoading] = useState<boolean>(false);
  const [isTrueTattooLoading, setIsTrueTattooLoading] =
    useState<boolean>(false);
  const [isAstrocartographyLoading, setIsAstrocartographyLoading] =
    useState<boolean>(false);
  const [isAstroDialogOpen, setIsAstroDialogOpen] = useState<boolean>(false);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [profile, setProfile] = useState<string>("");
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [reports, setReports] = useState<any[]>([]);

  const reportTypesMap: any = {
    time_analysis: t("timeAnalysis"),
    profession: t("professionAnalysis"),
    true_look: t("trueLookAnalysis"),
    true_tattoo: t("trueTattooAnalysis"),
    astrocartography: t("astrocartographyAnalysis"),
  };

  useEffect(() => {
    getProfiles();
    getReports();
    const interval = setInterval(getReports, 10000);
    return () => clearInterval(interval);
  }, []);

  const getProfiles = async () => {
    try {
      setIsLoading(true);
      const response = await getProfile();
      setProfiles(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getReports = async () => {
    try {
      const response = await getProfessionAnalysisReport();
      setReports(response?.items || []);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleDownload = async () => {
    if (!profile) return;
    try {
      setIsLoading(true);
      const language = i18n.language.toLowerCase();
      await downloadTimeAnalysis(profile, language);
      openSuccessNotification(
        t("timeReportStartedMsg"),
        t("reportGenerationStarted"),
      );
      getReports();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrueLookDownload = async () => {
    if (!profile) return;
    try {
      setIsTrueLookLoading(true);
      const language = i18n.language.toLowerCase();
      await downloadTrueLookReport(profile, language);
      openSuccessNotification(
        t("trueLookReportStartedMsg"),
        t("reportGenerationStarted"),
      );
      getReports();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsTrueLookLoading(false);
    }
  };

  const handleTrueTattooDownload = async () => {
    if (!profile) return;
    try {
      setIsTrueTattooLoading(true);
      const language = i18n.language.toLowerCase();
      await downloadTrueTattooReport(profile, language);
      openSuccessNotification(
        t("trueTattooReportStartedMsg"),
        t("reportGenerationStarted"),
      );
      getReports();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsTrueTattooLoading(false);
    }
  };

  const handleAstrocartographyDownload = async (filters: AstroReportFilters) => {
    if (!profile) return;
    try {
      setIsAstrocartographyLoading(true);
      const language = i18n.language.toLowerCase();
      await downloadAstrocartographyReport(profile, language, filters);
      setIsAstroDialogOpen(false);
      openSuccessNotification(
        t("astrocartographyReportStartedMsg"),
        t("reportGenerationStarted"),
      );
      getReports();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsAstrocartographyLoading(false);
    }
  };

  const handleProfessionDownload = async () => {
    if (!profile) return;
    try {
      setIsProfessionLoading(true);
      const language = i18n.language.toLowerCase();
      await downloadProfessionAnalysis(profile, language);
      openSuccessNotification(
        t("professionReportStartedMsg"),
        t("reportGenerationStarted"),
      );
      getReports();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsProfessionLoading(false);
    }
  };

  const getHeader = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { sm: "center", xs: "baseline" },
          width: "100%",
        }}
        pb={3}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ cursor: "pointer", width: "fit-content" }}
          onClick={() => history.goBack()}
        >
          {isMobile ? (
            <img src={backArrow} height={"20px"} alt="Module not found..." />
          ) : (
            <img src={backArrow} height={"25px"} alt="Module not found..." />
          )}
        </Box>
        <Typography sx={classes.italicTypo}>{t("analysisReport")}</Typography>
      </Box>
    );
  };

  const getActions = () => {
    return (
      <Box sx={classes.contentWrapper}>
        {/* Profile selector */}
        <Box sx={classes.selectorSection}>
          <Box sx={classes.selectorLabelRow}>
            <PersonSearchOutlinedIcon sx={classes.selectorIcon} />
            <Typography sx={classes.selectorLabel}>
              {t("selectProfile")}
            </Typography>
          </Box>
          <Autocomplete
            sx={classes.autoComplete}
            options={profiles || []}
            getOptionLabel={(option: any) => option.name || ""}
            renderInput={(params) => (
              <TextField {...params} placeholder={t("searchProfile")} />
            )}
            onChange={(event, value) => {
              setProfile(value?.profileId || "");
              setSelectedProfile(value || null);
            }}
          />
        </Box>

        {/* Download buttons */}
        <Box sx={classes.downloadSection}>
          <Box sx={classes.downloadButtonsRow}>
            <Box sx={classes.downloadButtonWrapper}>
              <CustomButton
                label={
                  <Box sx={classes.downloadButtonLabel}>
                    <FileDownloadOutlinedIcon fontSize="small" />
                    {t("timeAnalysis")}
                  </Box>
                }
                type={strings.PRIMARY}
                onClick={handleDownload}
                disabled={!profile || isLoading}
                customClasses={classes.downloadButton}
              />
            </Box>
            <Box sx={classes.downloadButtonWrapper}>
              <CustomButton
                label={
                  <Box sx={classes.downloadButtonLabel}>
                    <FileDownloadOutlinedIcon fontSize="small" />
                    {t("professionAnalysis")}
                  </Box>
                }
                type={strings.PRIMARY}
                onClick={handleProfessionDownload}
                disabled={!profile || isProfessionLoading}
                customClasses={classes.downloadButton}
              />
            </Box>
            <Box sx={classes.downloadButtonWrapper}>
              <CustomButton
                label={
                  <Box sx={classes.downloadButtonLabel}>
                    <FileDownloadOutlinedIcon fontSize="small" />
                    {t("trueLookAnalysis")}
                  </Box>
                }
                type={strings.PRIMARY}
                onClick={handleTrueLookDownload}
                disabled={!profile || isTrueLookLoading}
                customClasses={classes.downloadButton}
              />
            </Box>
            <Box sx={classes.downloadButtonWrapper}>
              <CustomButton
                label={
                  <Box sx={classes.downloadButtonLabel}>
                    <FileDownloadOutlinedIcon fontSize="small" />
                    {t("trueTattooAnalysis")}
                  </Box>
                }
                type={strings.PRIMARY}
                onClick={handleTrueTattooDownload}
                disabled={!profile || isTrueTattooLoading}
                customClasses={classes.downloadButton}
              />
            </Box>
            <Box sx={classes.downloadButtonWrapper}>
              <CustomButton
                label={
                  <Box sx={classes.downloadButtonLabel}>
                    <FileDownloadOutlinedIcon fontSize="small" />
                    {t("astrocartographyAnalysis")}
                  </Box>
                }
                type={strings.PRIMARY}
                onClick={() => setIsAstroDialogOpen(true)}
                disabled={!profile || isAstrocartographyLoading}
                customClasses={classes.downloadButton}
              />
            </Box>
          </Box>
          {!profile && (
            <Typography sx={classes.hintText}>
              {t("selectProfileToDownload")}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const getProfileName = (profileId: string) => {
    const found = profiles.find((p) => p.profileId === profileId);
    return found?.name || profileId;
  };

  const getReportsTable = () => {
    return (
      <Box sx={classes.tableSection}>
        <Typography sx={classes.tableSectionTitle}>
          {t("reportsHistory")}
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={classes.headerCell}>
                  {t("profileName")}
                </TableCell>
                <TableCell sx={classes.headerCell}>{t("reportType")}</TableCell>
                <TableCell sx={classes.headerCell}>{t("language")}</TableCell>
                <TableCell sx={classes.headerCell}>{t("updatedOn")}</TableCell>
                <TableCell sx={classes.headerCell}>{t("status")}</TableCell>
                <TableCell sx={classes.headerCell}>{t("action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow sx={classes.noDataRow}>
                  <TableCell colSpan={5} sx={classes.noDataCell}>
                    {t("noReports")}
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell sx={classes.dataCell}>
                      {getProfileName(report.profileId)}
                    </TableCell>
                    <TableCell sx={classes.dataCell}>
                      {reportTypesMap[report.reportType] || "-"}
                    </TableCell>
                    <TableCell sx={classes.dataCell}>
                      {report.language?.toUpperCase() || "-"}
                    </TableCell>
                    <TableCell sx={classes.dataCell}>
                      {report.updatedOn
                        ? formatDateTime(report.updatedOn, i18n.language)
                        : "-"}
                    </TableCell>
                    <TableCell sx={classes.dataCell}>
                      {capitalizeWords(report.status) || "-"}
                    </TableCell>
                    <TableCell sx={classes.actionCell}>
                      {report.url ? (
                        <Tooltip title={t("download")}>
                          <IconButton
                            component="a"
                            href={report.url}
                            target="_blank"
                            rel="noreferrer"
                            size="small"
                            sx={{ color: "rgba(144, 83, 246, 0.9)" }}
                          >
                            <FileDownloadOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    );
  };

  const getTimeAnalysis = () => {
    return (
      <>
        <Box sx={classes.mainContainer}>
          <Box sx={classes.mainBox}>
            <Box sx={classes.subBox}>
              <Box sx={classes.scrollStyle}>
                <Container maxWidth="xl" sx={{ px: 1 }}>
                  {getHeader()}
                  {getActions()}
                  {getReportsTable()}
                </Container>
              </Box>
            </Box>
          </Box>
        </Box>
        <AstrocartographyFilterDialog
          open={isAstroDialogOpen}
          onClose={() => setIsAstroDialogOpen(false)}
          onDownload={handleAstrocartographyDownload}
          profileName={selectedProfile?.name}
          isDownloading={isAstrocartographyLoading}
        />
        <CustomLoader
          isLoading={
            isLoading ||
            isProfessionLoading ||
            isTrueLookLoading ||
            isTrueTattooLoading ||
            isAstrocartographyLoading
          }
        />
      </>
    );
  };

  return getTimeAnalysis();
};

export default TimeAnalysis;
